import React from 'react';
import FaqItem from '../components/FaqItem';
import CallToAction from '../components/CallToAction';

const FaqPage: React.FC = () => {
  const generalFaqs = [
    {
      question: "How often should my pool be cleaned?",
      answer: "Most residential pools benefit from weekly service to maintain proper chemical balance and cleanliness. However, pools with lower usage or automatic cleaners might be fine with bi-weekly service. During your initial consultation, we'll help determine the best schedule for your specific pool and usage patterns."
    },
    {
      question: "Do I need to be home when you clean my pool?",
      answer: "No, you don't need to be home. As long as we have access to your pool area, we can perform our service while you're away. Many of our customers provide us with gate codes or keys to access the pool area."
    },
    {
      question: "How long does a typical pool cleaning take?",
      answer: "A standard pool cleaning service typically takes 30-45 minutes, depending on the size of your pool and its current condition. More extensive services like green-to-clean treatments may take longer."
    }
  ];

  const serviceFaqs = [
    {
      question: "What's included in your weekly cleaning service?",
      answer: "Our weekly service includes water testing and chemical balancing, skimming debris from the surface, emptying skimmer and pump baskets, brushing walls and steps, vacuuming the pool floor, checking and backwashing the filter as needed, and inspecting all equipment for proper operation."
    },
    {
      question: "What is your 'Green to Clean' service?",
      answer: "Our Green to Clean service is designed to restore pools that have turned green from algae or have been neglected. It includes advanced shock treatments, algaecide applications, multiple filter cleanings, thorough brushing of all surfaces, vacuuming, and water rebalancing. The process typically takes 2-5 days, depending on the severity."
    },
    {
      question: "Do you repair pool equipment?",
      answer: "Yes, we repair and replace all types of pool equipment, including pumps, filters, heaters, salt systems, automation systems, and more. Our technicians are factory-trained and experienced with most major brands."
    },
    {
      question: "What is your short-term rental pool service?",
      answer: "Our rental property service is designed specifically for vacation homes and short-term rentals. It includes flexible scheduling around bookings, pre-arrival inspections and cleanings, post-guest servicing, on-call service for guest issues, and detailed logs for property managers."
    }
  ];

  const chemicalFaqs = [
    {
      question: "What chemicals do you use?",
      answer: "We use commercial-grade, high-quality pool chemicals that are both effective and gentle on your pool equipment. Our standard treatment includes chlorine (or salt system management), pH balancers, alkalinity adjusters, stabilizers, and algaecides as needed. We can also accommodate special requests for alternative sanitization methods."
    },
    {
      question: "Is it safe to swim after you service my pool?",
      answer: "In most cases, it's safe to swim about 30 minutes after regular service. However, if we perform a shock treatment or add certain chemicals, we'll leave specific instructions about when it's safe to use the pool again."
    },
    {
      question: "Can you convert my pool to a salt system?",
      answer: "Yes, we can convert traditional chlorine pools to salt systems. Salt systems generate their own chlorine, resulting in softer water that's gentler on skin, eyes, and swimwear. Contact us for a consultation if you're interested in this conversion."
    },
    {
      question: "How do you handle pool water testing?",
      answer: "We perform on-site testing of your pool water during each visit using professional-grade testing equipment. We check chlorine levels, pH, alkalinity, stabilizer, and other factors as needed. For more complex issues, we can send water samples to a lab for comprehensive analysis."
    }
  ];

  const costFaqs = [
    {
      question: "How much does pool service cost?",
      answer: "Our service pricing depends on several factors, including pool size, location, and the specific services needed. Weekly service typically ranges from $120-$180 per month. Bi-weekly service is a monthly charge of $80-$100. For an accurate quote tailored to your specific pool, please contact us."
    },
    {
      question: "Do you offer any discounts or promotions?",
      answer: "Yes! New customers receive their first month of regular service FREE. We also offer referral bonuses and occasional seasonal promotions. Contact us for current offers."
    },
    {
      question: "Is there a contract requirement?",
      answer: "We don't require long-term contracts for our standard service. We believe our service quality will keep you as a customer without binding agreements. For special circumstances or commercial properties, we offer contract options if desired."
    },
    {
      question: "What forms of payment do you accept?",
      answer: "We accept all major credit cards, ACH bank transfers, and checks. For regular service customers, we offer convenient auto-billing options to simplify payment."
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative bg-purple-900 py-20">
        <div 
          className="absolute inset-0 z-0 opacity-30" 
          style={{
            backgroundImage: 'url("https://images.pexels.com/photos/261394/pexels-photo-261394.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Get answers to common questions about our pool cleaning and maintenance services.
          </p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* General Questions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-purple-900 mb-6 pb-2 border-b border-gray-200">
                General Questions
              </h2>
              <div className="space-y-2">
                {generalFaqs.map((faq, index) => (
                  <FaqItem 
                    key={index} 
                    question={faq.question} 
                    answer={faq.answer} 
                    defaultOpen={index === 0}
                  />
                ))}
              </div>
            </div>

            {/* Service Questions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-purple-900 mb-6 pb-2 border-b border-gray-200">
                Our Services
              </h2>
              <div className="space-y-2">
                {serviceFaqs.map((faq, index) => (
                  <FaqItem 
                    key={index} 
                    question={faq.question} 
                    answer={faq.answer} 
                  />
                ))}
              </div>
            </div>

            {/* Chemical Questions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-purple-900 mb-6 pb-2 border-b border-gray-200">
                Pool Chemicals & Water Quality
              </h2>
              <div className="space-y-2">
                {chemicalFaqs.map((faq, index) => (
                  <FaqItem 
                    key={index} 
                    question={faq.question} 
                    answer={faq.answer} 
                  />
                ))}
              </div>
            </div>

            {/* Pricing Questions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-purple-900 mb-6 pb-2 border-b border-gray-200">
                Pricing & Billing
              </h2>
              <div className="space-y-2">
                {costFaqs.map((faq, index) => (
                  <FaqItem 
                    key={index} 
                    question={faq.question} 
                    answer={faq.answer} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-purple-900 mb-4">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Contact us directly and our team will be happy to provide the information you need.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="/quote"
              className="px-6 py-3 bg-orange-500 text-white font-bold rounded-md hover:bg-orange-600 transition-colors duration-200 text-center"
            >
              Contact Us
            </a>
            <a
              href="tel:+18008194612"
              className="px-6 py-3 bg-purple-700 text-white font-bold rounded-md hover:bg-purple-800 transition-colors duration-200 text-center"
            >
              Call (800) 819-4612
            </a>
          </div>
        </div>
      </section>

      <CallToAction />
    </div>
  );
};

export default FaqPage;